import { PayGatewayOptions, PayloftGatewayMessage } from "./types";
import { validateGatewayOptions, isTrustedMessage } from "./utils/security";

const DEFAULT_GATEWAY_URL = "https://checkout.payloft.com";

export interface VanillaPayOptions extends PayGatewayOptions { }

class PayloftPopManager {
  private overlay: HTMLDivElement | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private keydownListener: ((event: KeyboardEvent) => void) | null = null;
  private originalBodyOverflow: string = "";
  private redirectTimer: ReturnType<typeof setTimeout> | null = null;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  public open(options: VanillaPayOptions): void {
    // 1. Security & Option Validation
    validateGatewayOptions(options);

    // If already open, close previous instance
    this.close();

    const {
      reference,
      publicKey,
      gatewayUrl = DEFAULT_GATEWAY_URL,
      onSuccess,
      onClose,
      onError,
      onReady,
      customStyles,
      hideCloseButton = true,
      closeOnBackdropClick = true,
      metadata,
      redirectDelay = 5000,
      isSandbox = false,
    } = options;

    const cleanGatewayUrl = (gatewayUrl || DEFAULT_GATEWAY_URL).replace(/\/+$/, "");
    let gatewayOrigin = "*";
    try {
      gatewayOrigin = new URL(cleanGatewayUrl).origin;
    } catch (e) {
      gatewayOrigin = "*";
    }

    const parentOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const queryParams = new URLSearchParams();
    queryParams.set("ref", reference);
    queryParams.set("parentOrigin", parentOrigin);
    queryParams.set("embed", "true");
    if (publicKey) queryParams.set("publicKey", publicKey);

    if (metadata) {
      Object.entries(metadata).forEach(([key, val]) => {
        queryParams.set(`meta_${key}`, String(val));
      });
    }

    const basePath = isSandbox && !cleanGatewayUrl.includes("/sandbox-pay")
      ? `${cleanGatewayUrl}/sandbox-pay/card`
      : cleanGatewayUrl;

    const iframeSrc = `${basePath}/${encodeURIComponent(reference)}?${queryParams.toString()}`;

    // Create Overlay
    const overlay = document.createElement("div");
    overlay.id = "payloft-checkout-overlay";
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(15, 23, 42, 0.65);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      padding: 16px;
      box-sizing: border-box;
      opacity: 0;
      transition: opacity 0.2s ease-out;
    `;

    // Create Modal Container
    const modal = document.createElement("div");
    modal.style.cssText = `
      position: relative;
      width: 100%;
      max-width: 780px;
      height: 650px;
      max-height: 95vh;
      background-color: #ffffff;
      border-radius: 0px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      transform: scale(0.98);
      transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    `;

    // Create Spinner Loader
    const loader = document.createElement("div");
    loader.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #ffffff;
      z-index: 5;
      gap: 12px;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    loader.innerHTML = `
      <div style="width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top-color: #059669; border-radius: 50%; animation: payloft-spin 0.8s linear infinite;"></div>
      <span style="font-size: 14px; color: #64748b;">Loading Payloft Checkout...</span>
    `;

    // Add Keyframes if not present
    if (!document.getElementById("payloft-styles")) {
      const styleTag = document.createElement("style");
      styleTag.id = "payloft-styles";
      styleTag.innerHTML = `
        @keyframes payloft-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(styleTag);
    }

    // Create Iframe
    const iframe = document.createElement("iframe");
    iframe.src = iframeSrc;
    iframe.title = "Payloft Checkout";
    iframe.allow = "camera; payment; geolocation";
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 0px;
      background-color: #ffffff;
    `;
    iframe.onload = () => {
      loader.style.display = "none";
    };

    modal.appendChild(loader);
    modal.appendChild(iframe);
    overlay.appendChild(modal);

    if (closeOnBackdropClick) {
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          this.close();
          if (onClose) onClose();
        }
      };
    }

    // Handle Message Listener
    this.messageListener = (event: MessageEvent) => {
      // Strict Security Verification
      if (!isTrustedMessage(event, iframe.contentWindow, gatewayOrigin)) {
        return;
      }

      const data = event.data as PayloftGatewayMessage;
      if (!data || typeof data !== "object") return;

      switch (data.type) {
        case "pay:ready":
        case "PAYLOFT_EMBED_READY":
          loader.style.display = "none";
          if (onReady) onReady();
          break;

        case "pay:success":
        case "PAYLOFT_TRANSACTION_SUCCESS":
          loader.style.display = "none";
          // Verify reference integrity if sent in payload
          if (data.payload?.reference && data.payload.reference !== reference) {
            console.warn(
              `[Payloft Security] Warning: Response reference mismatch (expected ${reference}, received ${data.payload.reference})`
            );
          }
          if (onSuccess) {
            onSuccess(data.payload || { reference, status: "success" });
          }
          // Do not close immediately; wait for redirect to callback URL
          if (!this.fallbackTimer) {
            this.fallbackTimer = setTimeout(() => {
              this.close();
            }, 15000);
          }
          break;

        case "pay:redirecting":
        case "PAYLOFT_TRANSACTION_REDIRECTING":
          loader.style.display = "none";
          if (this.fallbackTimer) {
            clearTimeout(this.fallbackTimer);
            this.fallbackTimer = null;
          }
          this.showRedirectOverlay(
            modal,
            data.payload?.status === "expired"
              ? "Transaction Expired..."
              : "Processing..."
          );
          if (!this.redirectTimer) {
            this.redirectTimer = setTimeout(() => {
              this.close();
            }, redirectDelay);
          }
          break;

        case "pay:close":
        case "PAYLOFT_TRANSACTION_CANCELLED":
          this.close();
          if (onClose) onClose();
          break;

        case "pay:error":
        case "PAYLOFT_TRANSACTION_FAILED":
          loader.style.display = "none";
          if (onError) {
            onError(
              data.payload || {
                reference,
                message: "Transaction failed or error occurred",
              }
            );
          }
          break;

        default:
          break;
      }
    };
    window.addEventListener("message", this.messageListener);

    // ESC Key Listener
    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnBackdropClick) {
        this.close();
        if (onClose) onClose();
      }
    };
    window.addEventListener("keydown", this.keydownListener);

    // Prevent body scrolling
    this.originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    document.body.appendChild(overlay);
    this.overlay = overlay;

    // Trigger entrance animation
    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
      modal.style.transform = "scale(1)";
    });
  }

  private showRedirectOverlay(modal: HTMLElement, text: string): void {
    if (modal.querySelector(".payloft-redirect-overlay")) return;
    const overlay = document.createElement("div");
    overlay.className = "payloft-redirect-overlay";
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: #ffffff;
      z-index: 50;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      box-sizing: border-box;
    `;
    overlay.innerHTML = `
      <div style="width: 42px; height: 42px; border: 3px solid #e2e8f0; border-top-color: #00aeef; border-radius: 50%; animation: payloft-spin 0.8s linear infinite; margin-bottom: 16px;"></div>
      <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #0f172a; font-family: system-ui, -apple-system, sans-serif; text-align: center;">${text}</h3>
      <p style="margin: 0; font-size: 14px; color: #64748b; font-family: system-ui, -apple-system, sans-serif; text-align: center;">Finalizing transaction, closing in a moment...</p>
    `;
    modal.appendChild(overlay);
  }

  public close(): void {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
      this.messageListener = null;
    }
    if (this.keydownListener) {
      window.removeEventListener("keydown", this.keydownListener);
      this.keydownListener = null;
    }
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
      this.overlay = null;
    }
    if (this.originalBodyOverflow !== undefined) {
      document.body.style.overflow = this.originalBodyOverflow;
    }
  }

  public setup(options: VanillaPayOptions) {
    return {
      openIframe: () => this.open(options),
      close: () => this.close(),
    };
  }
}

const PayloftPop = new PayloftPopManager();
export default PayloftPop;
